import React, { useState } from 'react';
import { Button } from '../UI/button';
import { Input } from '../UI/input';
import { Label } from '../UI/label';
import { Textarea } from '../UI/textarea';
import { Loader2, ArrowLeft } from 'lucide-react';

/**
 * COMPOSANT: FORMULAIRE DE PAIEMENT
 * 
 * Formulaire adapté selon le mode de paiement sélectionné
 */

const PaymentForm = ({
  paymentMethod,
  amount,
  onSubmit,
  onCancel,
  submitting = false
}) => {
  const [formData, setFormData] = useState({
    externalRef: '',
    phoneNumber: '',
    notes: ''
  });

  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user types
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validation selon la méthode de paiement
    if (['ORANGE_MONEY', 'MTN_MONEY', 'MOOV_MONEY', 'WAVE'].includes(paymentMethod)) {
      if (!formData.phoneNumber) {
        newErrors.phoneNumber = 'Numéro de téléphone requis';
      } else if (!/^[0-9]{8,15}$/.test(formData.phoneNumber.replace(/\s/g, ''))) {
        newErrors.phoneNumber = 'Numéro de téléphone invalide';
      }
    }

    if (paymentMethod === 'CARTE_BANCAIRE') {
      // La validation de carte sera gérée par un service de paiement tiers
      // Pour l'instant on accepte juste une référence
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const getPaymentMethodLabel = () => {
    const labels = {
      CARTE_BANCAIRE: 'Carte Bancaire',
      ORANGE_MONEY: 'Orange Money',
      MTN_MONEY: 'MTN Mobile Money',
      MOOV_MONEY: 'Moov Money',
      WAVE: 'Wave',
      ESPECES: 'Espèces',
      VIREMENT: 'Virement Bancaire',
      CHEQUE: 'Chèque'
    };
    return labels[paymentMethod] || paymentMethod;
  };

  const formatAmount = (amt) => {
    return new Intl.NumberFormat('fr-FR').format(amt) + ' FCFA';
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* En-tête */}
      <div className="bg-primary/10 p-4 rounded-lg">
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium">Mode de paiement</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onCancel}
            disabled={submitting}
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Changer
          </Button>
        </div>
        <p className="text-lg font-bold mt-1">{getPaymentMethodLabel()}</p>
        <div className="flex justify-between items-center mt-2 pt-2 border-t">
          <span className="text-sm">Montant à payer</span>
          <span className="text-xl font-bold text-primary">{formatAmount(amount)}</span>
        </div>
      </div>

      {/* Champs spécifiques selon la méthode */}
      {['ORANGE_MONEY', 'MTN_MONEY', 'MOOV_MONEY', 'WAVE'].includes(paymentMethod) && (
        <div className="space-y-4">
          <div>
            <Label htmlFor="phoneNumber">Numéro de téléphone</Label>
            <Input
              id="phoneNumber"
              type="tel"
              placeholder="Ex: 771234567"
              value={formData.phoneNumber}
              onChange={(e) => handleChange('phoneNumber', e.target.value)}
              disabled={submitting}
              className={errors.phoneNumber ? 'border-red-500' : ''}
            />
            {errors.phoneNumber && (
              <p className="text-sm text-red-500 mt-1">{errors.phoneNumber}</p>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              Vous recevrez une notification pour confirmer le paiement
            </p>
          </div>

          <div>
            <Label htmlFor="externalRef">Référence de transaction (optionnel)</Label>
            <Input
              id="externalRef"
              type="text"
              placeholder="Ex: OM20250111001"
              value={formData.externalRef}
              onChange={(e) => handleChange('externalRef', e.target.value)}
              disabled={submitting}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Référence fournie par l'opérateur après paiement
            </p>
          </div>
        </div>
      )}

      {paymentMethod === 'CARTE_BANCAIRE' && (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              🔒 Vous serez redirigé vers une page de paiement sécurisée pour effectuer votre paiement par carte bancaire.
            </p>
          </div>
        </div>
      )}

      {paymentMethod === 'ESPECES' && (
        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-800">
              💵 Ce paiement sera enregistré comme effectué en espèces. Assurez-vous de conserver votre reçu.
            </p>
          </div>
        </div>
      )}

      {paymentMethod === 'VIREMENT' && (
        <div className="space-y-4">
          <div>
            <Label htmlFor="externalRef">Référence de virement</Label>
            <Input
              id="externalRef"
              type="text"
              placeholder="Ex: VIR20250111001"
              value={formData.externalRef}
              onChange={(e) => handleChange('externalRef', e.target.value)}
              disabled={submitting}
            />
          </div>
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 text-sm">
            <p className="font-semibold text-indigo-900 mb-2">Coordonnées bancaires :</p>
            <p className="text-indigo-800">Banque: [NOM BANQUE]</p>
            <p className="text-indigo-800">IBAN: [IBAN]</p>
            <p className="text-indigo-800">BIC/SWIFT: [BIC]</p>
          </div>
        </div>
      )}

      {paymentMethod === 'CHEQUE' && (
        <div className="space-y-4">
          <div>
            <Label htmlFor="externalRef">Numéro de chèque</Label>
            <Input
              id="externalRef"
              type="text"
              placeholder="Ex: CHQ1234567"
              value={formData.externalRef}
              onChange={(e) => handleChange('externalRef', e.target.value)}
              disabled={submitting}
            />
          </div>
        </div>
      )}

      {/* Notes (optionnel) */}
      <div>
        <Label htmlFor="notes">Notes (optionnel)</Label>
        <Textarea
          id="notes"
          placeholder="Ajoutez des notes ou commentaires..."
          value={formData.notes}
          onChange={(e) => handleChange('notes', e.target.value)}
          disabled={submitting}
          rows={3}
        />
      </div>

      {/* Boutons d'action */}
      <div className="flex gap-3 pt-4 border-t">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={submitting}
          className="flex-1"
        >
          Annuler
        </Button>
        <Button
          type="submit"
          disabled={submitting}
          className="flex-1"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Traitement...
            </>
          ) : (
            'Confirmer le paiement'
          )}
        </Button>
      </div>

      {/* Avertissement sécurité */}
      <div className="text-xs text-center text-muted-foreground">
        <p>🔒 Vos informations de paiement sont sécurisées et protégées</p>
      </div>
    </form>
  );
};

export default PaymentForm;
